import { Table as AntdTable, TableProps as AntdTableProps } from "antd";
import type { AnyObject } from "antd/es/_util/type";
import type { SortOrder } from "antd/es/table/interface";
import classNames from "classnames";
import { ArrowDown, ArrowUp, Filter } from "lucide-react";
import React, { useMemo } from "react";
import styles from "./styles.module.scss";

declare module "antd/es/table/interface" {
  export interface ColumnType<RecordType = AnyObject> {
    filterCount?: number;
  }
}

export interface TableProps<RecordType = any> extends AntdTableProps<RecordType> {
  wrapperClassName?: string;
}

interface TableInterface {
  <RecordType extends object = any>(
    props: TableProps<RecordType>
  ): React.ReactElement | null;
  Summary: typeof AntdTable.Summary;
  Column: typeof AntdTable.Column;
  ColumnGroup: typeof AntdTable.ColumnGroup;
  SELECTION_COLUMN: typeof AntdTable.SELECTION_COLUMN;
  EXPAND_COLUMN: typeof AntdTable.EXPAND_COLUMN;
}

const Table: TableInterface = Object.assign(
  <RecordType extends object = any>({
    className,
    wrapperClassName,
    columns,
    ...props
  }: TableProps<RecordType>) => {
    const processedColumns = useMemo(() => {
      if (!columns) return columns;

      const processColumns = (cols: any[]): any[] =>
        cols.map((col) => {
        const nextChildren = Array.isArray(col.children)
          ? processColumns(col.children)
          : col.children;
        const hasFilter =
          !!col.filterDropdown ||
          !!col.filters ||
          typeof col.filterCount === "number";

        const count = col.filterCount || 0;
        const originalFilterIcon = col.filterIcon;
        const originalOnHeaderCell = col.onHeaderCell;
        const originalSortIcon = col.sortIcon;

        const isSorted =
          col.sortOrder === "ascend" || col.sortOrder === "descend";
        const isFiltered =
          col.filtered ??
          (count > 0 ||
            isSorted ||
            col.isFiltered === true);

        if (!hasFilter && !col.sorter) {
          return nextChildren === col.children
            ? col
            : { ...col, children: nextChildren };
        }

        const filterIcon = hasFilter
          ? (filtered: boolean) => {
              let baseIcon: React.ReactNode = null;
              if (typeof originalFilterIcon === "function") {
                baseIcon = originalFilterIcon(filtered);
              } else if (originalFilterIcon) {
                baseIcon = originalFilterIcon;
              }
              if (!baseIcon) {
                baseIcon = <Filter size={11} strokeWidth={2.5} />;
              }

              return (
                <div
                  className={classNames(
                    styles.filterIconWrapper,
                    isFiltered && styles.activeFilterWrapper
                  )}
                >
                  {baseIcon}
                  {count > 0 && (
                    <span className={styles.filterCountBadge}>
                      {count > 9 ? "9+" : count}
                    </span>
                  )}
                </div>
              );
            }
          : originalFilterIcon;

        const renderSortIcon = isSorted ? (
          <div
            className={styles.sortIconBtn}
            title={
              col.sortOrder === "ascend"
                ? "Sorted ascending — click to reverse"
                : "Sorted descending — click to reverse"
            }
            onClick={(e) => {
              e.stopPropagation();
              const nextOrder: SortOrder =
                col.sortOrder === "ascend" ? "descend" : "ascend";

              if (typeof props.onChange === "function") {
                props.onChange(
                  {} as any,
                  {} as any,
                  {
                    column: col,
                    columnKey: col.key,
                    field: col.dataIndex,
                    order: nextOrder,
                  },
                  { action: "sort", currentDataSource: [] as any }
                );
              }
            }}
          >
            {col.sortOrder === "ascend" ? (
              <ArrowUp size={11} strokeWidth={2.5} />
            ) : (
              <ArrowDown size={11} strokeWidth={2.5} />
            )}
          </div>
        ) : null;

        const originalTitle = col.title;
        const title = (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span
              className={styles.headerTitleText}
              onClick={(e) => {
                e.stopPropagation();
                if (hasFilter) {
                  const th = e.currentTarget.closest("th");
                  const trigger = th?.querySelector<HTMLElement>(
                    ".ant-table-filter-trigger"
                  );
                  if (trigger) {
                    trigger.click();
                  }
                }
              }}
            >
              {typeof originalTitle === "function"
                ? originalTitle(col)
                : originalTitle}
            </span>
            {renderSortIcon}
          </div>
        );

        const onHeaderCell = (columnData: any, index?: number) => {
          const origProps = originalOnHeaderCell
            ? (originalOnHeaderCell as any)(columnData, index)
            : {};

          return {
            ...origProps,
            className: classNames(
              origProps.className,
              isFiltered && "ant-table-cell-filtered",
              isSorted && "ant-table-column-sort"
            ),
            onClick: (e: React.MouseEvent<HTMLElement>) => {
              origProps.onClick?.(e);
            },
            style: { ...origProps.style },
          };
        };

        return {
          ...col,
          ...(Array.isArray(nextChildren) ? { children: nextChildren } : {}),
          sorter: false,
          title,
          showSorterTooltip: false,
          filtered: isFiltered,
          filterIcon,
          onHeaderCell,
        };
      });

      return processColumns(columns as any[]);
    }, [columns]);

    return (
      <div className={classNames(styles.tableWrapper, wrapperClassName)}>
        <AntdTable<RecordType>
          className={classNames(styles.customTable, className)}
          columns={processedColumns}
          showSorterTooltip={false}
          {...props}
        />
      </div>
    );
  },
  {
    Summary: AntdTable.Summary,
    Column: AntdTable.Column,
    ColumnGroup: AntdTable.ColumnGroup,
    SELECTION_COLUMN: AntdTable.SELECTION_COLUMN,
    EXPAND_COLUMN: AntdTable.EXPAND_COLUMN,
  }
);

export { Table };
